import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import {
  CalendarOptions,
  FullCalendarComponent
} from '@fullcalendar/angular';

import dayGridPlugin from '@fullcalendar/angular/daygrid';
import timeGridPlugin from '@fullcalendar/angular/timegrid';
import interactionPlugin from '@fullcalendar/angular/interaction';
import themePlugin from '@fullcalendar/angular/themes/monarch';

import { CourseService } from 'src/app/services/course/course.service';
import { AdminService } from 'src/app/services/admin/admin.service';
import { ShortCourseOrderModel } from 'src/app/core/ShortCourseOrderModel';
import { OrderService } from 'src/app/services/order/order.service';
import { ActivatedRoute, Router } from '@angular/router';


/* type CalendarView =
  | 'dayGridMonth'
  | 'timeGridWeek'
  | 'timeGridDay'; */
//search extendedProps and remove allDay: true,

//calendar layout config
type CalendarView =
  | 'dayGridMonth'
  | 'dayGridWeek'
  | 'dayGridDay';


//interface
interface CalendarCourseFilter {
  id: string;
  name: string;
  selected: boolean;
  count: number;
  backgroundColor: string;
  textColor: string;
}


@Component({
  selector: 'app-x-course-calendar',
  templateUrl: './x-course-calendar.component.html',
  styleUrl: './x-course-calendar.component.scss'
})
export class XCourseCalendarComponent implements OnInit, OnDestroy {
  // Store course calendar reference
  @ViewChild('courseCalendar')
  courseCalendar!: FullCalendarComponent;

  // Control data updated message
  showDataUpdatedMessage = false;

  // Store participant phone
  participantPhone = '';


  // Store calendar title
  calendarTitle = '';

  // Store active calendar view
  activeCalendarView: CalendarView =
    'dayGridMonth';

  // Store selected date
  selectedDate = '';

  // Store all calendar courses
  allCourses: any[] = [];

  // Store filtered calendar courses
  filteredCourses: any[] = [];

  // Track calendar loading state
  isCalendarLoading = false;


  // Store course type filters
  courseTypeFilters: CalendarCourseFilter[] = [];

  // Store selected location
  selectedLocation = '';

  // Store available locations
  availableLocations: string[] = [];

  // Control filter panel visibility
  isFilterPanelVisible = true;


  // Store selected course details
  selectedCourseDetails: any = null;

  // Control course modal visibility
  showCourseModal = false;


  // Store all UOC courses
  allUOCCourses: any[] = [];

  // Store all VOC courses
  allVOCCourses: any[] = [];

  // Store selected additional UOCs
  selectedAdditionalUOCs: any[] = [];

  // Store selected additional VOCs
  selectedAdditionalVOCs: any[] = [];

  // Store selected UOC ID
  selectedUOCId: number | null = null;

  // Store selected VOC ID
  selectedVOCId: number | null = null;


  // Control participant modal visibility
  showParticipantModal = false;

  // Store participant given name
  participantGivenName = '';

  // Store participant family name
  participantFamilyName = '';

  // Store participant email
  participantEmail = '';

  // Store participant form error
  participantFormError = '';

  // Track participant submission
  isSubmittingParticipant = false;


  // Store booking success message
  bookingSuccessMessage = '';

  // Store booking error message
  bookingErrorMessage = '';


  // Store selected course ID
  selectedCourseId: number | null = null;

  // Store selected occurrence ID
  selectedOccurrenceId: number | null = null;

  //Store occurance date
  selectedOccurrenceDate: string | null = null;

  // Prevent opening URL occurrence more than once
  private selectedOccurrenceOpened = false;

  // Track selected course filter state
  private selectedCourseFilterApplied = false;

  //store is Not High Risk Course
  isNotHighRiskCourse: boolean = false;

  //store show Enquiry  success Popup
  showEnquirySuccessPopup: boolean = false;
  // ============================================================
  // CALENDAR OPTIONS
  // ============================================================

  calendarOptions: CalendarOptions = {

    plugins: [
      themePlugin,
      dayGridPlugin,
      timeGridPlugin,
      interactionPlugin
    ],

    initialView: 'dayGridMonth',

    headerToolbar: false,

    height: 'auto',

    fixedWeekCount: false,

    showNonCurrentDates: true,

    displayEventTime: false,

    dayMaxEvents: 2,

    moreLinkText: (count) =>
      `+${count} more`,

    eventDisplay: 'block',

    nowIndicator: true,

    locale: 'en-AU',

    slotMinTime: '07:00:00',

    slotMaxTime: '19:00:00',

    slotDuration: '00:30:00',

    allDaySlot: true,

    events: [],


    // ==========================================================
    // DATE RANGE CHANGE
    // ==========================================================

    datesSet: (info) => {

      /*
       * Set asynchronously to avoid:
       * ExpressionChangedAfterItHasBeenCheckedError
       */
      setTimeout(() => {

        this.calendarTitle =
          info.view.title;

        this.activeCalendarView =
          info.view.type as CalendarView;

      });


      const startDate =
        this.toDateKey(
          info.start
        );


      /*
       * FullCalendar end date is exclusive.
       */
      const endDate =
        new Date(info.end);

      endDate.setDate(
        endDate.getDate() - 1
      );


      this.loadCourseCalendar(
        startDate,
        this.toDateKey(endDate)
      );
    },


    // ==========================================================
    // DATE CLICK
    // ==========================================================

    dateClick: (info) => {

      this.selectCalendarDate(
        info.dateStr
      );
    },


    // ==========================================================
    // EVENT CLICK
    // ==========================================================

    eventClick: (info) => {

      const selectedCourse =
        this.filteredCourses.find(
          course =>
            String(course.id) ===
            String(info.event.id)
        );


      if (selectedCourse) {

        this.openCourseDetails(
          selectedCourse
        );
      }
    },


    // ==========================================================
    // SELECTED DAY
    // ==========================================================

    dayCellClass: (info) => {

      return (
        this.toDateKey(info.date) ===
        this.selectedDate
      )
        ? 'ecb-course-calendar-selected-day'
        : '';
    },


    // ==========================================================
    // EVENT MOUNT
    // ==========================================================

    eventDidMount: (info) => {

      const renderedCourse =
        this.filteredCourses.find(
          course =>
            String(course.id) ===
            String(info.event.id)
        );


      const backgroundColor =
        renderedCourse?.backgroundColor ??
        '#eef2ff';

      const textColor =
        renderedCourse?.textColor ??
        '#4338ca';


      this.applyEventElementColors(
        info.el,
        backgroundColor,
        textColor
      );
    },


    // ==========================================================
    // EVENT CONTENT
    // ==========================================================

    eventContent: (info) => {

      const renderedCourse =
        this.filteredCourses.find(
          course =>
            String(course.id) ===
            String(info.event.id)
        );


      const backgroundColor =
        renderedCourse?.backgroundColor ??
        '#eef2ff';

      const textColor =
        renderedCourse?.textColor ??
        '#4338ca';


      const eventWrapper =
        document.createElement('div');

      eventWrapper.className =
        'ecb-course-calendar-event-content';

      eventWrapper.style.width =
        '100%';

      eventWrapper.style.height =
        '100%';

      eventWrapper.style.boxSizing =
        'border-box';

      eventWrapper.style.backgroundColor =
        backgroundColor;

      eventWrapper.style.color =
        textColor;


      const codeElement =
        document.createElement('strong');

      codeElement.className =
        'ecb-course-calendar-event-code';

      codeElement.textContent =
        info.event.extendedProps[
        'calendarCode'
        ] ?? '';


      const nameElement =
        document.createElement('span');

      nameElement.className =
        'ecb-course-calendar-event-name';

      nameElement.textContent =
        info.event.extendedProps[
        'courseName'
        ] ?? info.event.title;


      eventWrapper.append(
        codeElement,
        nameElement
      );


      return {
        domNodes: [
          eventWrapper
        ]
      };
    }
  };



  constructor(private router: Router,
    private courseService: CourseService,
    private adminService: AdminService,
    private orderService: OrderService,
    private route: ActivatedRoute
  ) {
  }


  // ============================================================
  // INIT
  // ============================================================
  ngOnInit(): void {

    this.route.queryParamMap.subscribe(
      params => {

        // =====================================================
        // COURSE ID
        // =====================================================

        const courseId =
          Number(
            params.get('courseId')
          );

        this.selectedCourseId =
          courseId > 0
            ? courseId
            : null;


        // =====================================================
        // OCCURRENCE ID
        // =====================================================

        const occurrenceId =
          Number(
            params.get('occurrenceId')
          );

        this.selectedOccurrenceId =
          occurrenceId > 0
            ? occurrenceId
            : null;


        // =====================================================
        // OCCURRENCE DATE
        // =====================================================

        this.selectedOccurrenceDate =
          params.get(
            'occurrenceDate'
          );


        // =====================================================
        // START CALENDAR AT OCCURRENCE MONTH
        // =====================================================

        if (this.selectedOccurrenceDate) {

          this.calendarOptions = {
            ...this.calendarOptions,

            initialDate:
              this.selectedOccurrenceDate
          };
        }

      }
    );


    // Load default UOC courses
    this.loadUOCDefCourses();
  }

  // ============================================================
  // OPEN SELECTED OCCURRENCE FROM URL
  // ============================================================

  private openSelectedOccurrenceFromUrl(): void {

    // No occurrence passed
    if (
      !this.selectedOccurrenceId
    ) {
      return;
    }


    // Do not automatically open again
    if (
      this.selectedOccurrenceOpened
    ) {
      return;
    }


    // Find occurrence
    const selectedOccurrence =
      this.allCourses.find(
        course =>
          Number(course.id) ===
          Number(this.selectedOccurrenceId)
      );


    // Occurrence not found
    if (
      !selectedOccurrence
    ) {
      return;
    }


    // Mark as opened
    this.selectedOccurrenceOpened =
      true;


    // Open existing course modal
    this.openCourseDetails(
      selectedOccurrence
    );
  }


  // Handle component destroy
  ngOnDestroy(): void {
  }

  // ============================================================
  // APPLY SELECTED COURSE FILTER
  // ============================================================

  private applySelectedCourseFilter(): void {

    // No course passed from details page
    if (!this.selectedCourseId) {
      return;
    }


    // Only apply automatically once
    if (this.selectedCourseFilterApplied) {
      return;
    }


    const courseExists =
      this.courseTypeFilters.some(
        filter =>
          Number(filter.id) ===
          Number(this.selectedCourseId)
      );


    if (!courseExists) {
      return;
    }


    // Select only the passed course
    this.courseTypeFilters =
      this.courseTypeFilters.map(
        filter => ({

          ...filter,

          selected:
            Number(filter.id) ===
            Number(this.selectedCourseId)

        })
      );


    this.selectedCourseFilterApplied =
      true;
  }
  // ============================================================
  // LOAD CALENDAR COURSES
  // ============================================================
  loadCourseCalendar(
    startDate: string,
    endDate: string
  ): void {

    // ---------------------------------------
    // 1. Load existing/current data immediately
    // ---------------------------------------
    this.loadCurrentCalendarData(
      startDate,
      endDate,
      true
    );



  }

  private loadCurrentCalendarData(
    startDate: string,
    endDate: string,
    showLoader: boolean = false,
    showUpdatedMessage: boolean = false
  ): void {

    if (showLoader) {
      this.isCalendarLoading = true;
    }

    this.courseService
      .getXCourseCalendarList(
        startDate,
        endDate
      )
      .subscribe({

        next: (response: any[]) => {

          console.log(
            'Course Calendar Response:',
            response
          );

          this.allCourses =
            (response || [])
              .map((item) => {

                const course =
                  item?.course ??
                  item?.Course;

                const occurance =
                  item?.occurance ??
                  item?.Occurance;

                if (
                  !course ||
                  !occurance
                ) {
                  return null;
                }

                const courseCode =
                  course.courseCode ??
                  course.CourseCode ??
                  '';

                return {

                  id:
                    occurance.id ??
                    occurance.Id,

                  courseId:
                    course.id ??
                    course.Id,

                  courseCode:
                    '',

                  courseName:
                    course.courseTitle ??
                    course.CourseTitle ??
                    '',

                  description:
                    this.formatCourseDescription(
                      course.description ??
                      course.Description
                    ),

                  totalAmount:
                    course.totalAmount ??
                    course.TotalAmount ??
                    0,

                  currencyCode:
                    course.currencyCode ??
                    course.CurrencyCode ??
                    '',

                  documentTypes:
                    course.documentTypes ??
                    course.DocumentTypes ??
                    '',

                  startDate:
                    occurance.startDate ??
                    occurance.StartDate,

                  endDate:
                    occurance.endDate ??
                    occurance.EndDate,

                  registrationStartDate:
                    occurance.registrationStartDate ??
                    occurance.RegistrationStartDate ??
                    null,

                  registrationEndDate:
                    occurance.registrationEndDate ??
                    occurance.RegistrationEndDate ??
                    null,

                  location:
                    occurance.location ??
                    occurance.Location ??
                    '',

                  capacity:
                    occurance.capacity ??
                    occurance.Capacity ??
                    0,

                  availableSeats:
                    occurance.availableSeats ??
                    occurance.AvailableSeats ??
                    0,

                  vettrackOccuranceId:
                    occurance.vettrackOccuranceId ??
                    occurance.VettrackOccuranceId ??
                    null,

                  vettrackJobNumber:
                    occurance.vettrackJobNumber ??
                    occurance.VettrackJobNumber ??
                    null,

                  backgroundColor:
                    occurance.backgroundColor ??
                    occurance.BackgroundColor ??
                    '#808080',

                  textColor:
                    occurance.textColor ??
                    occurance.TextColor ??
                    '#FFFFFF'
                };

              })
              .filter(
                item => item !== null
              );


          // Rebuild filter values
          this.buildFilters();


          // Keep selected course filter
          this.applySelectedCourseFilter();


          // Apply current filters
          this.applyFilters();


          // Open selected occurrence from URL
          this.openSelectedOccurrenceFromUrl();


          if (showLoader) {
            this.isCalendarLoading = false;
          }


          // Show small temporary message only
          // after the background sync reload
          if (showUpdatedMessage) {
            this.showCalendarUpdatedMessage();
          }
        },

        error: (error) => {

          console.error(
            'Calendar Error:',
            error
          );

          if (showLoader) {

            this.allCourses = [];

            this.filteredCourses = [];

            this.courseTypeFilters = [];

            this.availableLocations = [];

            this.updateCalendarEvents();

            this.isCalendarLoading = false;
          }

          // If this was the second/background refresh,
          // DON'T clear the existing calendar.
        }

      });
  }


  private formatCourseDescription(
    description: string | null | undefined
  ): string {

    if (!description) {
      return '';
    }

    let formatted =
      description
        .replace(
          /^UNIT DESCRIPTOR:\s*/i,
          ''
        )
        .trim();

    // Separate Licensing section
    formatted = formatted.replace(
      /\s*LICENSING\/REGULATORY INFORMATION:\s*/i,
      '\n\nLICENSING / REGULATORY INFORMATION\n\n'
    );

    // Put list items on separate lines
    formatted = formatted.replace(
      /\s+-\s+/g,
      '\n- '
    );

    // Clean excessive spaces
    formatted = formatted.replace(
      /[ \t]+/g,
      ' '
    );

    // Clean excessive empty lines
    formatted = formatted.replace(
      /\n{3,}/g,
      '\n\n'
    );

    return formatted.trim();
  }

  // ============================================================
  // LOAD UOC
  // ============================================================

  loadUOCDefCourses(): void {

    this.adminService
      .getXCourseList('GET_ALL')
      .subscribe({

        next: (response: any[]) => {

          const courses =
            response || [];



          this.allUOCCourses =
            courses;


        },


        error: (error) => {

          console.error(
            'UOC Error:',
            error
          );
        }

      });
  }


  loadUOCCourses(): void {

    let type = 'GET_ALL';
    /*  if (this.isNotHighRiskCourse) {
       type = 'GET_UOC_ONLY';
     } */
    this.adminService
      .getXCourseList(type)
      .subscribe({
        next: (response: any[]) => {
          const courses = response || [];
          this.allUOCCourses = courses;
        },
        error: (error) => {
          console.error(
            'UOC Error:',
            error
          );
        }
      });
  }


  // ============================================================
  // LOAD VOC
  // ============================================================

  loadVOCCourses(): void {

    this.adminService
      .getXVOCList('GET_ALL')
      .subscribe({

        next: (response: any[]) => {

          const courses = response || [];


          /* 
                    if (this.isNotHighRiskCourse) {
          
                      this.allVOCCourses = courses.filter(course =>
                        course?.vocCode
                          ?.toUpperCase()
                          .startsWith('WP')
                      );
          
                    } else { */

          this.allVOCCourses = courses;
          /*  } */
        },

        error: (error) => {

          console.error(
            'VOC Error:',
            error
          );
        }
      });
  }


  // ============================================================
  // ADD UOC
  // ============================================================

  addSelectedUOC(): void {

    if (
      this.selectedUOCId === null
    ) {
      return;
    }


    const selectedCourse =
      this.allUOCCourses.find(
        course =>
          Number(course.id) ===
          Number(this.selectedUOCId)
      );


    if (!selectedCourse) {
      return;
    }


    /*
     * Do not allow primary course
     * to be selected again.
     */
    if (
      Number(selectedCourse.id) ===
      Number(
        this.selectedCourseDetails?.courseId
      )
    ) {

      this.selectedUOCId = null;

      return;
    }


    const alreadySelected =
      this.selectedAdditionalUOCs.some(
        course =>
          Number(course.id) ===
          Number(selectedCourse.id)
      );


    if (!alreadySelected) {

      this.selectedAdditionalUOCs =
        [
          ...this.selectedAdditionalUOCs,
          selectedCourse
        ];
    }


    this.selectedUOCId =
      null;
  }

  get availableUOCCourses(): any[] {
    return this.allUOCCourses.filter(course => {

      // Hide primary selected course
      if (
        Number(course.id) ===
        Number(this.selectedCourseDetails?.courseId)
      ) {
        return false;
      }

      // Hide already added UOC courses
      const alreadySelected =
        this.selectedAdditionalUOCs.some(
          selected =>
            Number(selected.id) === Number(course.id)
        );

      return !alreadySelected;
    });
  }
  // ============================================================
  // REMOVE UOC
  // ============================================================

  removeSelectedUOC(courseId: number): void {

    const course = this.selectedAdditionalUOCs.find(
      item =>
        Number(item.id) ===
        Number(courseId)
    );

    // Primary calendar selected course cannot be removed.
    if (course?.isPrimaryCalendarCourse) {
      return;
    }

    this.selectedAdditionalUOCs =
      this.selectedAdditionalUOCs.filter(
        item =>
          Number(item.id) !==
          Number(courseId)
      );
  }

  // ============================================================
  // ADD VOC
  // ============================================================

  addSelectedVOC(): void {

    if (
      this.selectedVOCId === null
    ) {
      return;
    }


    const selectedVOC =
      this.allVOCCourses.find(
        voc =>
          Number(voc.id) ===
          Number(this.selectedVOCId)
      );


    if (!selectedVOC) {
      return;
    }


    const alreadySelected =
      this.selectedAdditionalVOCs.some(
        voc =>
          Number(voc.id) ===
          Number(selectedVOC.id)
      );


    if (!alreadySelected) {

      this.selectedAdditionalVOCs =
        [
          ...this.selectedAdditionalVOCs,
          selectedVOC
        ];
    }


    this.selectedVOCId =
      null;
  }

  get availableVOCCourses(): any[] {
    return this.allVOCCourses.filter(voc => {

      const alreadySelected =
        this.selectedAdditionalVOCs.some(
          selected =>
            Number(selected.id) === Number(voc.id)
        );

      return !alreadySelected;
    });
  }
  // ============================================================
  // REMOVE VOC
  // ============================================================

  removeSelectedVOC(
    vocId: number
  ): void {

    this.selectedAdditionalVOCs =
      this.selectedAdditionalVOCs.filter(
        voc =>
          Number(voc.id) !==
          Number(vocId)
      );
  }


  // ============================================================
  // HAS ADDITIONAL COURSES
  // ============================================================

  get hasAdditionalCourses(): boolean {

    return (
      this.additionalUOCCount > 0 ||
      this.selectedAdditionalVOCs.length > 0
    );
  }


  // ============================================================
  // PRIMARY ACTION
  // ============================================================

  get primaryActionText(): string {

    const amount =
      Number(
        this.selectedCourseDetails?.totalAmount ?? 0
      );

    if (
      this.hasAdditionalCourses ||
      amount <= 0
    ) {
      return 'Enquiry Now';
    }

    return 'Pay Now';
  }

  // ============================================================
  // PARTICIPANT BUTTON
  // ============================================================

  get participantActionText(): string {

    const amount =
      Number(
        this.selectedCourseDetails?.totalAmount ?? 0
      );

    if (
      this.hasAdditionalCourses ||
      amount <= 0
    ) {
      return 'Send Enquiry';
    }

    return `Pay A$${amount.toFixed(2)}`;
  }

  // ============================================================
  // TOTAL COURSE COUNT
  // ============================================================

  get totalSelectedCourses(): number {

    return (
      1 +
      this.additionalUOCCount +
      this.selectedAdditionalVOCs.length
    );
  }


  // ============================================================
  // BUILD FILTERS
  // ============================================================

  buildFilters(): void {

    const existingSelections =
      new Map(
        this.courseTypeFilters.map(
          filter => [
            filter.id,
            filter.selected
          ]
        )
      );


    const map =
      new Map<string, any>();


    this.allCourses.forEach(course => {

      const key =
        String(course.courseId);


      if (!map.has(key)) {

        map.set(
          key,
          {
            id: key,
            name: course.courseName,
            count: 1,
            backgroundColor:
              course.backgroundColor,
            textColor:
              course.textColor
          }
        );

      } else {

        map.get(key).count++;
      }
    });


    this.courseTypeFilters =
      Array.from(
        map.values()
      ).map(item => ({

        ...item,

        selected:
          existingSelections.has(item.id)
            ? existingSelections.get(
              item.id
            )!
            : true
      }));


    this.availableLocations =
      Array.from(
        new Set(
          this.allCourses
            .map(
              course =>
                course.location
            )
            .filter(
              location =>
                !!location
            )
        )
      ).sort();
  }


  // ============================================================
  // APPLY FILTERS
  // ============================================================

  applyFilters(): void {

    const selectedIds =
      this.courseTypeFilters
        .filter(
          filter =>
            filter.selected
        )
        .map(
          filter =>
            filter.id
        );


    this.filteredCourses =
      this.allCourses.filter(
        course => {

          const matchesCourse =
            selectedIds.includes(
              String(course.courseId)
            );


          const matchesLocation =
            !this.selectedLocation ||
            course.location ===
            this.selectedLocation;


          return (
            matchesCourse &&
            matchesLocation
          );
        }
      );


    this.updateCalendarEvents();
  }


  // ============================================================
  // CLEAR FILTERS
  // ============================================================

  clearFilters(): void {

    this.courseTypeFilters
      .forEach(
        filter =>
          filter.selected = true
      );


    this.selectedLocation =
      '';


    this.filteredCourses =
      [...this.allCourses];


    this.updateCalendarEvents();
  }


  // ============================================================
  // TOGGLE FILTER
  // ============================================================

  toggleCourseFilter(
    filter: CalendarCourseFilter
  ): void {

    filter.selected =
      !filter.selected;
  }


  // ============================================================
  // UPDATE EVENTS
  // ============================================================

  updateCalendarEvents(): void {

    const calendarEvents =
      this.filteredCourses
        .filter(
          course =>
            course.id &&
            course.startDate
        )
        .map(
          course => ({

            id:
              String(course.id),

            title:
              course.courseName,

            start:
              course.startDate,

            end:
              course.endDate,

            allDay: true,

            extendedProps: {

              calendarCode:
                course.courseCode,

              courseName:
                course.courseName,

              courseId:
                course.courseId,

              description:
                course.description,

              location:
                course.location,

              capacity:
                course.capacity,

              availableSeats:
                course.availableSeats,

              totalAmount:
                course.totalAmount,

              currencyCode:
                course.currencyCode,

              documentTypes:
                course.documentTypes,

              registrationStartDate:
                course.registrationStartDate,

              registrationEndDate:
                course.registrationEndDate,

              vettrackOccuranceId:
                course.vettrackOccuranceId,

              vettrackJobNumber:
                course.vettrackJobNumber
            }
          })
        );


    this.calendarOptions = {
      ...this.calendarOptions,
      events:
        calendarEvents
    };
  }


  // ============================================================
  // NAVIGATION
  // ============================================================

  goToToday(): void {

    this.courseCalendar
      .getApi()
      .today();
  }


  goToPrevious(): void {

    this.courseCalendar
      .getApi()
      .prev();
  }


  goToNext(): void {

    this.courseCalendar
      .getApi()
      .next();
  }


  changeCalendarView(
    view: CalendarView
  ): void {

    this.activeCalendarView =
      view;


    this.courseCalendar
      .getApi()
      .changeView(view);
  }


  // ============================================================
  // DATE
  // ============================================================

  toDateKey(
    date: Date
  ): string {

    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(
        2,
        '0'
      );

    const day =
      String(
        date.getDate()
      ).padStart(
        2,
        '0'
      );


    return `${year}-${month}-${day}`;
  }

  // ============================================================
  // SELECT CALENDAR DATE
  // ============================================================


  selectCalendarDate(
    date: string
  ): void {

    this.selectedDate =
      date;
  }


  // ============================================================
  // OPEN COURSE DETAILS
  // ============================================================


  openCourseDetails(course: any): void {
    debugger
    this.selectedCourseDetails = course;


    // Try to get the matching UOC from the loaded UOC list.
    const matchingUOC = this.allUOCCourses.find(
      uoc =>
        Number(uoc.id) ===
        Number(course.courseId)
    );


    /*
     * Add the calendar selected course into the UOC selection list.
     * Mark it as primary/locked so the user cannot remove it.
     */
    const primaryUOC = matchingUOC
      ? {
        ...matchingUOC,
        isPrimaryCalendarCourse: true
      }
      : {
        id: course.courseId,
        courseCode: course.courseCode,
        courseTitle: course.courseName,
        description: course.description,
        totalAmount: course.totalAmount,
        currencyCode: course.currencyCode,
        documentTypes: course.documentTypes,
        isPrimaryCalendarCourse: true
      };


    this.selectedAdditionalUOCs = [
      primaryUOC
    ];


    this.selectedAdditionalVOCs = [];

    this.selectedUOCId = null;

    this.selectedVOCId = null;

    this.bookingSuccessMessage = '';

    this.bookingErrorMessage = '';

    this.showCourseModal = true;

    /*     this.isNotHighRiskCourse =
          this.selectedCourseDetails?.courseCode
            ?.toUpperCase()
            .startsWith('TLILIC') ?? false; */

    this.loadUOCCourses();
    this.loadVOCCourses();
  }



  // ============================================================
  // CLOSE COURSE DETAILS
  // ============================================================

  closeCourseDetails(): void {

    this.showCourseModal =
      false;
  }


  // ============================================================
  // OPEN PARTICIPANT DETAILS
  // ============================================================

  openParticipantDetails(): void {

    if (!this.selectedCourseDetails) {
      return;
    }


    this.participantGivenName = '';
    this.participantFamilyName = '';
    this.participantEmail = '';
    this.participantFormError = '';
    this.participantPhone = ''


    /*
     * Close course details modal
     * and open participant modal.
     */
    this.showCourseModal = false;
    this.showParticipantModal = true;
  }


  // ============================================================
  // BACK TO COURSE DETAILS
  // ============================================================

  backToCourseDetails(): void {

    this.showParticipantModal = false;

    this.showCourseModal = true;
  }


  // ============================================================
  // CLOSE PARTICIPANT
  // ============================================================

  closeParticipantModal(): void {

    this.showParticipantModal =
      false;

    this.participantFormError =
      '';
  }


  // ============================================================
  // PARTICIPANT SUBMIT
  // ============================================================

  submitParticipantDetails(): void {

    this.participantFormError =
      '';


    if (
      !this.participantGivenName.trim()
    ) {

      this.participantFormError =
        'Please enter the participant given name.';

      return;
    }


    if (!this.participantEmail.trim()) {

      this.participantFormError = 'Please enter the participant email address.';

      return;
    }

    if (!this.participantPhone.trim()) {
      this.participantFormError =
        'Please enter the participant contact number.';

      return;
    }



    if (this.hasAdditionalCourses || this.selectedCourseDetails?.totalAmount <= 0) {

      this.sendBookingEnquiry();

      return;
    }


    this.processSingleCoursePayment();
  }


  // ============================================================
  // CREATE BOOKING JSON
  // ============================================================

  createBookingJson(): any {

    return {

      bookingType:
        this.hasAdditionalCourses
          ? 'MULTI_COURSE_ENQUIRY'
          : Number(this.selectedCourseDetails?.totalAmount ?? 0) > 0
            ? 'SINGLE_UOC_PAYMENT'
            : 'SINGLE_UOC_ENQUIRY',


      participant: {
        contactNumber: this.participantPhone,
        givenName: this.participantGivenName,
        familyName: this.participantFamilyName,
        email: this.participantEmail,
      },


      primaryCourse: {

        courseId:
          this.selectedCourseDetails
            ?.courseId,

        courseCode:
          this.selectedCourseDetails
            ?.courseCode,

        courseTitle:
          this.selectedCourseDetails
            ?.courseName,

        description:
          this.selectedCourseDetails
            ?.description,

        totalAmount:
          this.selectedCourseDetails
            ?.totalAmount,

        currencyCode:
          this.selectedCourseDetails
            ?.currencyCode,

        documentTypes:
          this.selectedCourseDetails
            ?.documentTypes,


        occurrence: {

          occurrenceId:
            this.selectedCourseDetails
              ?.id,

          startDate:
            this.selectedCourseDetails
              ?.startDate,

          endDate:
            this.selectedCourseDetails
              ?.endDate,

          registrationStartDate:
            this.selectedCourseDetails
              ?.registrationStartDate,

          registrationEndDate:
            this.selectedCourseDetails
              ?.registrationEndDate,

          location:
            this.selectedCourseDetails
              ?.location,

          capacity:
            this.selectedCourseDetails
              ?.capacity,

          availableSeats:
            this.selectedCourseDetails
              ?.availableSeats,

          vettrackOccuranceId:
            this.selectedCourseDetails
              ?.vettrackOccuranceId,

          vettrackJobNumber:
            this.selectedCourseDetails
              ?.vettrackJobNumber
        }
      },


      additionalUOCCourses:
        this.additionalUOCsOnly.map(
          course => ({

            courseId:
              course.id,

            courseCode:
              course.courseCode,

            courseTitle:
              course.courseTitle,

            description:
              course.description,

            totalAmount:
              course.totalAmount,

            currencyCode:
              course.currencyCode,

            documentTypes:
              course.documentTypes

          })
        ),


      vocCourses:
        this.selectedAdditionalVOCs
          .map(
            voc => ({

              vocId:
                voc.id,

              vocCode:
                voc.vocCode,

              vocName:
                voc.vocName,

              description:
                voc.description,

              documentTypes:
                voc.documentTypes,

              fkParentCourseId:
                voc.fkParentCourseId
            })
          ),


      totalCourses:
        this.totalSelectedCourses,

      createdDate:
        new Date().toISOString()
    };
  }


  // ============================================================
  // SEND ENQUIRY
  // ============================================================

  sendBookingEnquiry(): void {


    const bookingJson =
      this.createBookingJson();


    const enquiryModel = {

      id: 0,

      body:
        JSON.stringify(
          bookingJson
        ),

      sentBy:
        this.participantEmail,

      reviewedBy:
        null,

      reviewedDate:
        null,

      isApproved:
        false,

      isActive:
        true,
      clientType: 'GENERAL'
    };


    this.isSubmittingParticipant =
      true;


    this.adminService
      .setXEnquiry(
        enquiryModel,
        'INSERT'
      )
      .subscribe({

        next: (response) => {



          this.isSubmittingParticipant =
            false;

          this.showParticipantModal =
            false;

          this.bookingSuccessMessage =
            'Thank you for your enquiry! We have received your enquiry successfully. One of our team members will be in touch with you shortly.';
          ; this.showEnquirySuccessPopup = true;
        },


        error: (error) => {

          console.error(
            'Enquiry Error:',
            error
          );


          this.isSubmittingParticipant =
            false;

          this.participantFormError =
            'Unable to send the enquiry. Please try again.';
        }
      });
  }

  // ============================================================
  // CLOSE ENQUIRY SUCCESS POPUP
  // ============================================================
  closeEnquirySuccessPopup(): void {

    this.showEnquirySuccessPopup = false;

    this.router.navigate(['/']);
  }

  // ============================================================
  // SINGLE COURSE PAYMENT
  // ============================================================

  processSingleCoursePayment(): void {
    if (!this.selectedCourseDetails || !this.participantGivenName?.trim() || !this.participantEmail?.trim()) {
      return;
    }


    const request: ShortCourseOrderModel = {

      id: 0,

      // Main course Id
      vetTrackId: Number(
        this.selectedCourseDetails.courseId
      ),

      shortCourseTitle: this.selectedCourseDetails.courseName,

      studentGivenName: this.participantGivenName.trim(),

      studentSingleName: this.participantFamilyName?.trim() || '',

      studentEmail: this.participantEmail.trim(),

      totalAmount:
        Number(
          this.selectedCourseDetails.totalAmount || 0
        ),

      currencyCode:
        this.selectedCourseDetails.currencyCode || 'AUD',

      stripeSessionId:
        null,

      stripePaymentIntentId:
        null,

      paymentStatus:
        'Pending',

      createdDate:
        null,

      noOfSeatedLeft:
        Number(
          this.selectedCourseDetails.availableSeats || 0
        ),

      type:
        'REGULAR',

      jobNo:
        this.selectedCourseDetails.vettrackJobNumber || null,

      startDate:
        this.selectedCourseDetails.startDate,

      endDate:
        this.selectedCourseDetails.endDate,

      duration:
        ''
    };


    this.isSubmittingParticipant =
      true;


    this.orderService
      .createStripeShortCheckout(request)
      .subscribe({

        next: response => {

          this.isSubmittingParticipant =
            false;


          if (response?.checkoutUrl) {

            sessionStorage.setItem('ecb_pending_booking',
              JSON.stringify(
                {

                  OrderId:
                    response.orderId,

                  studentGivenName:
                    this.participantGivenName,

                  studentFamilyName:
                    this.participantFamilyName,

                  studentEmail:
                    this.participantEmail,

                  totalAmount:
                    Number(
                      this.selectedCourseDetails.totalAmount || 0
                    ),

                  currencyCode:
                    this.selectedCourseDetails.currencyCode || 'AUD',

                  courseId:
                    this.selectedCourseDetails.courseId,

                  courseName:
                    this.selectedCourseDetails.courseName,

                  courseCode:
                    this.selectedCourseDetails.courseCode,

                  occurrenceId:
                    this.selectedCourseDetails.id,

                  startDate:
                    this.selectedCourseDetails.startDate,

                  endDate:
                    this.selectedCourseDetails.endDate,

                  location:
                    this.selectedCourseDetails.location,

                  availableSeats:
                    this.selectedCourseDetails.availableSeats,

                  duration:
                    '',

                  vettrackOccuranceId:
                    this.selectedCourseDetails.vettrackOccuranceId,

                  vettrackJobNumber:
                    this.selectedCourseDetails.vettrackJobNumber
                }
              )
            );


            window.location.href =
              response.checkoutUrl;

          } else {

            console.error(
              'Stripe checkout URL was not returned.'
            );

            this.participantFormError =
              'Unable to start the payment. Please try again.';
          }
        },


        error: error => {

          this.isSubmittingParticipant =
            false;

          console.error(
            'Stripe checkout failed:',
            error
          );

          this.participantFormError =
            'Payment could not be started. Please try again.';
        }
      });
  }


  // ============================================================
  // APPLY COLORS TO CELLS
  // ============================================================

  applyEventElementColors(
    element: HTMLElement,
    backgroundColor: string,
    textColor: string
  ): void {

    element.style.backgroundColor =
      backgroundColor;

    element.style.borderColor =
      backgroundColor;

    element.style.color =
      textColor;
  }


  // ============================================================
  // GET COURSE COLOR
  // ============================================================
  getCourseColor(
    courseCode: string,
    index: number
  ): {
    backgroundColor: string;
    textColor: string;
  } {

    const colors = [

      {
        backgroundColor:
          '#5f28e6',
        textColor:
          '#ffffff'
      },

      {
        backgroundColor:
          '#ef3340',
        textColor:
          '#ffffff'
      },

      {
        backgroundColor:
          '#f3d900',
        textColor:
          '#302c00'
      },

      {
        backgroundColor:
          '#278a5b',
        textColor:
          '#ffffff'
      },

      {
        backgroundColor:
          '#8c8c8c',
        textColor:
          '#ffffff'
      },

      {
        backgroundColor:
          '#9a009a',
        textColor:
          '#ffffff'
      }
    ];


    /*
     * Create same colour for same course code.
     */
    let hash = 0;

    for (
      let i = 0;
      i < courseCode.length;
      i++
    ) {

      hash =
        courseCode.charCodeAt(i) +
        ((hash << 5) - hash);
    }


    const colorIndex =
      Math.abs(hash) %
      colors.length;


    return courseCode
      ? colors[colorIndex]
      : colors[
      index %
      colors.length
      ];
  }


  // ============================================================
  // ADDITIONAL UOC HELPERS
  // ============================================================

  // get additional UOC s
  get additionalUOCsOnly(): any[] {
    return this.selectedAdditionalUOCs.filter(
      course => !course.isPrimaryCalendarCourse
    );
  }

  //get additional uoc counts
  get additionalUOCCount(): number {
    return this.additionalUOCsOnly.length;
  }


  //show calendar updated message
  private showCalendarUpdatedMessage(): void {

    this.showDataUpdatedMessage = true;

    setTimeout(() => {

      this.showDataUpdatedMessage = false;

    }, 3000);
  }

    showingOccurrencePicker = false;

    
}